import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { EASE } from "@/components/Reveal";

const PAGES = {
  mentions: {
    title: "Mentions légales",
    sections: [
      {
        h: "Éditeur du site",
        p: "BABYLON — Conciergerie horlogère privée.\nSociété par actions simplifiée (SAS) au capital de 10 001 €.\nSIREN 107 368 607 — SIRET 107 368 607 00019.\nCode NAF/APE : 4791B — Vente à distance sur catalogue spécialisé.\nSiège social : La Rochelle (17), France.\nRCS La Rochelle.\nContact : Quentin.pontif@gmail.com\nDirecteur de la publication : Quentin Pontif.",
      },
      {
        h: "Hébergement",
        p: "Ce site est hébergé sur l'infrastructure cloud de la plateforme Emergent (emergent.sh). Les données sont hébergées au sein de l'Union européenne.",
      },
      {
        h: "Propriété intellectuelle",
        p: "L'ensemble des contenus du site (textes, identité visuelle, mise en page) est la propriété exclusive de BABYLON, sauf mention contraire. Les marques horlogères citées (Rolex, Patek Philippe, Audemars Piguet, Vacheron Constantin, Omega, etc.) appartiennent exclusivement à leurs titulaires respectifs. BABYLON est une conciergerie indépendante et n'est ni affiliée, ni agréée, ni sponsorisée par ces maisons.",
      },
      {
        h: "Crédits",
        p: "Photographies : Unsplash (licence Unsplash) — dont Patek Philippe Nautilus par Kent Lâm. Vidéo : Pexels (licence Pexels — utilisation libre, y compris commerciale).",
      },
    ],
  },
  privacy: {
    title: "Politique de confidentialité",
    sections: [
      {
        h: "Responsable du traitement",
        p: "BABYLON — Conciergerie horlogère privée.\nContact : Quentin.pontif@gmail.com.",
      },
      {
        h: "Données collectées",
        p: "Via le formulaire de demande de recherche : prénom, nom, adresse e-mail, numéro de téléphone (facultatif), ainsi que les informations relatives à votre recherche horlogère (marque, modèle, référence, budget, état, année, configuration, délai, message).",
      },
      {
        h: "Finalités et base légale",
        p: "Ces données sont utilisées exclusivement pour étudier et traiter votre demande de recherche de garde-temps, vous recontacter et vous présenter une proposition. Le traitement repose sur votre consentement (case à cocher du formulaire) et sur les mesures précontractuelles prises à votre demande.",
      },
      {
        h: "Destinataires et confidentialité",
        p: "Vos informations ne sont ni vendues, ni cédées à des tiers à des fins commerciales. Elles peuvent être partagées, uniquement lorsque nécessaire à votre recherche, avec des fournisseurs et professionnels de notre réseau, dans un cadre strictement confidentiel.",
      },
      {
        h: "Durée de conservation",
        p: "Vos données sont conservées pendant une durée maximale de 24 mois à compter de votre dernière demande, sauf obligation légale contraire.",
      },
      {
        h: "Vos droits",
        p: "Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez d'un droit d'accès, de rectification, d'effacement, de limitation, d'opposition et de portabilité de vos données. Pour exercer ces droits, écrivez à Quentin.pontif@gmail.com. Vous pouvez également introduire une réclamation auprès de la CNIL (cnil.fr).",
      },
      {
        h: "Cookies",
        p: "Ce site n'utilise aucun cookie publicitaire ni outil de suivi marketing. Seuls des éléments techniques strictement nécessaires au fonctionnement du site peuvent être utilisés.",
      },
    ],
  },
  cgv: {
    title: "Conditions générales de vente",
    sections: [
      {
        h: "Objet",
        p: "BABYLON agit en qualité de conciergerie horlogère privée : service de recherche, d'intermédiation et d'accompagnement à l'acquisition de garde-temps de luxe. BABYLON n'est pas une boutique en ligne et ne présente aucun stock disponible à la vente immédiate sur ce site.",
      },
      {
        h: "Processus de recherche et d'acquisition",
        p: "1. Le client transmet une demande de recherche via le formulaire.\n2. BABYLON active son réseau de fournisseurs et de professionnels.\n3. Lorsqu'un garde-temps correspondant est identifié, une proposition détaillée (description, état, prix, conditions d'acquisition) est présentée au client.\n4. Après validation expresse du client, BABYLON organise et suit le processus d'acquisition jusqu'à la réception du garde-temps.",
      },
      {
        h: "Prix et paiement",
        p: "Aucun paiement n'est exigé avant la présentation et la validation d'une proposition. Selon les conditions de la transaction, un paiement ou un acompte peut être demandé afin de sécuriser l'acquisition auprès du fournisseur. Les modalités de paiement (montant, échéancier, moyens acceptés) sont communiquées clairement au client avant toute validation.",
      },
      {
        h: "Délais",
        p: "Les délais de recherche et d'acquisition sont indicatifs : ils dépendent du modèle recherché, de sa disponibilité sur le marché et des conditions d'acquisition. BABYLON informe le client de l'avancement de sa recherche.",
      },
      {
        h: "Vérification des garde-temps",
        p: "Chaque garde-temps proposé fait l'objet d'un contrôle professionnel (état, cohérence des composants, documentation et provenance disponibles). Les informations recueillies sont communiquées au client en toute transparence avant validation.",
      },
      {
        h: "Indépendance",
        p: "BABYLON est une conciergerie indépendante. Les marques citées sur ce site appartiennent à leurs titulaires respectifs ; leur mention sert uniquement à illustrer les types de recherches possibles.",
      },
      {
        h: "Droit applicable",
        p: "Les présentes conditions sont soumises au droit français. Tout litige fera l'objet d'une recherche préalable de solution amiable.",
      },
    ],
  },
};

export default function LegalPage({ page }) {
  const content = PAGES[page];
  return (
    <main className="min-h-screen bg-obsidian text-ivory" data-testid={`legal-${page}`}>
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14 lg:py-20">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE }}>
          <Link
            to="/"
            className="inline-flex items-center gap-2.5 text-xs tracking-[0.2em] uppercase text-stone2 hover:text-gold transition-colors"
            data-testid="legal-back-link"
          >
            <ArrowLeft size={14} strokeWidth={1.5} /> Retour à Babylon
          </Link>
          <p className="mt-12 font-mono text-[10px] tracking-[0.35em] uppercase text-gold/80">Babylon</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl text-ivory">{content.title}</h1>
          <div className="mt-12 space-y-10">
            {content.sections.map((s) => (
              <section key={s.h}>
                <h2 className="font-display text-xl sm:text-2xl text-gold/90">{s.h}</h2>
                <p className="mt-4 text-sm sm:text-base text-stone2 leading-relaxed whitespace-pre-line">
                  {s.p}
                </p>
              </section>
            ))}
          </div>
          <p className="mt-16 pt-8 border-t border-white/[0.06] text-xs text-stone2/50">
            © 2026 BABYLON — Conciergerie horlogère privée. Dernière mise à jour : septembre 2026.
          </p>
        </motion.div>
      </div>
    </main>
  );
}
